import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useSelector } from 'react-redux'
import numeral from 'numeral'
import moment from 'moment'

import Table from '../../../Invoices/Table'

import styles from './styles.module.scss'

import { getCurrency } from '../../../../helpers/currencies'
import getUniqueArrayByKey from '../../../../helpers/getUniqueArrayByKey'

const columnHelper = createColumnHelper()

const formatNumber = (value, currency) => {
  return `${numeral(value).format('0,0.[00]')} ${currency}`
}

const getDuration = (minutes) => {
  const duartion = moment.duration(minutes, 'minutes')
  const hours = Math.floor(duartion.asHours())
  const mins = duartion.minutes()
  return `${hours}h ${mins}m`
}

const RevenueBySupplier = ({transactions}) => {
  const { t } = useTranslation()
      
  const companyInfo = useSelector(state => state.company.companyInfo)
  const currency = getCurrency(companyInfo?.currency)

  const data = useMemo(() => {
    const invoices = transactions.filter(tr => tr.TransactionType === 'INVOICE')
    const supplierMap = invoices.reduce((acc, transaction) => {
      const supplier = transaction['Counteragent.Name'] || 'Uncategorized'
      return {
        ...acc,
        [supplier]: acc[supplier] ? [...acc[supplier], transaction] : [transaction],
      }
    }, {})

    const totalAmount = invoices.reduce((acc, transaction) => acc + (transaction['Sum.Incoming'] || 0), 0)

    return Object.entries(supplierMap).map(([supplier, transactions]) => {
      const amount = transactions.reduce((acc, transaction) => acc + (transaction['Sum.Incoming'] || 0), 0)
      return {
        name: supplier,
        invoices: transactions.length,
        amount: transactions.reduce((acc, transaction) => acc + (transaction['Sum.Incoming'] || 0), 0),
        percentage: totalAmount > 0 ? (amount / totalAmount * 100) : 0,
      }
    })
  }, [transactions])

  const columns = useMemo(() => ([
    columnHelper.accessor('name', {
      id: 'name',
      header: t('Supplier'),
      enableSorting: false,
      cell: ({ getValue, row }) => {
        return `${getValue()}`
      }
    }),
    columnHelper.accessor('invoices', {
      id: 'invoices',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
      header: t('Invoices'),
    }),
    columnHelper.accessor('amount', {
      id: 'amount',
      enableSorting: false,
      cell: ({ getValue }) => formatNumber(getValue(), currency.symbol),
      header: t('Amount'),
    }),
    columnHelper.accessor('percentage', {
      id: 'percentage',
      enableSorting: false,
      cell: ({ getValue }) => `${numeral(getValue()).format('0.[00]')}%`,
      header: t('Percentage'),
    }),
  ]), [t, currency.symbol])

  const table = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{t('Revenue by supplier')}</div>
      </div>
      <Table
        table={table}
        columns={columns}
        wrapperStyle={{flex: 1}}
        headerClassName={styles.tableHeader} />
    </div>
  )
}

export default RevenueBySupplier
